import { AccountProperty, CompleteAccountProperty } from '../../../../../../../../generated-types';
import {
    BaseItem,
    GroupedAccountCardItem,
    GroupedItems,
    ProjectedAccountGroup,
} from '../../../../../../../../models/account';

type WithSortingKey = {
    sortingKey?: number;
};

export const BIG_NUMBER = 1_000_000;

export function compareProperties<T extends WithSortingKey>(a: T, b: T) {
    if ((a.sortingKey ?? BIG_NUMBER) < (b.sortingKey ?? BIG_NUMBER)) {
        return -1;
    }
    if ((b.sortingKey ?? BIG_NUMBER) < (a.sortingKey ?? BIG_NUMBER)) {
        return 1;
    }
    return 0;
}

export const getGroupedAccounts = <T extends BaseItem>(
    items: T[],
    groups: ProjectedAccountGroup[],
    properties: AccountProperty[],
) => {
    const sortedGroups = groups.sort(compareProperties);
    const defaultGroup = sortedGroups[0];

    const completeProperties: CompleteAccountProperty[] = properties.map((property) => {
        const groupId = property.accountGroup;
        const group = sortedGroups.find((group) => group.id.value === groupId?.value) ?? defaultGroup;
        return {
            ...property,
            accountGroup: group,
            sortingKey: property.sortingKey ?? BIG_NUMBER,
        };
    });

    const completeAccounts: [T, CompleteAccountProperty][] = items.map((accountCardItem) => {
        const property: CompleteAccountProperty = completeProperties.find(
            (prop) => prop.accountNumber.value === accountCardItem.accountNumber.value,
        ) ?? {
            accountGroup: defaultGroup,
            accountNumber: accountCardItem.accountNumber,
            frontpage: true,
            sortingKey: BIG_NUMBER,
        };

        return [
            {
                ...accountCardItem,
                id: accountCardItem.accountNumber.value,
                favorite: property.frontpage,
                sortKey: { value: property.sortingKey },
            },
            property,
        ];
    });

    const emptyGroupList: GroupedItems<T> = sortedGroups
        .reduce(
            (acc, cur) => [...acc, { name: cur.name, id: cur.id.value, values: [], isTemp: cur.isTemp }],
            [] as GroupedItems<GroupedAccountCardItem>,
        )
        .map((groupItem) => ({
            ...groupItem,
            values: completeAccounts
                .filter(([, property]) => property.accountGroup.id.value === groupItem.id)
                .sort(([, itemA], [, itemB]) => compareProperties(itemA, itemB))
                .map(([item]) => item),
        }));

    return emptyGroupList;
};
